<!-- @format -->

<template>
	<div class="residual-value-box">
		<div
			v-if="showScaledPanel"
			class="rv-scaled-panel bg-warning-light mb-6 px-10 py-3 rounded"
		>
			{{ scaledPanelText }}
		</div>

		<Message
			v-if="tableModeErrorMessage"
			class="mb-3"
			severity="error"
			icon="pi pi-exclamation-circle"
			:closable="false"
		>
			{{ $t(tableModeErrorMessage.message) }}
		</Message>

		<ResidualValuesTable v-if="unitTypeStore.hasRvt" />

		<div
			v-else
			class="mb-3"
		>
			<CoreTwoColumnsRow class="mb-3">
				<template #left>
					<CoreBorderWrapper class="flex-grow-1 flex-shrink-1 flex-basis-55">
						{{ $t('§contracts.depreciation_period') }}
						{{ isFieldRequired('rv_depreciation_period_years') }}
					</CoreBorderWrapper>
				</template>
				<template #right>
					<CoreDropdown
						class="flex-grow-1 flex-shrink-1 flex-basis-45"
						name="rvDepreciationPeriodYears"
						v-model="unitTypeStore.unitTypeData.rv_depreciation_period_years"
						:options="depreciationPeriodOptions"
						:sort="false"
						:validator="v$.rv_depreciation_period_years"
					/>
				</template>
			</CoreTwoColumnsRow>

			<CoreTwoColumnsRow class="mb-3">
				<template #left>
					<CoreBorderWrapper class="flex-grow-1 flex-shrink-1 flex-basis-55">
						{{ $t('§eq_type.residual_value_day_1') }}
						{{ isFieldRequired('residual_value_day_1') }}
					</CoreBorderWrapper>
				</template>
				<template #right>
					<div class="flex items-center gap-2 flex-grow-1 flex-shrink-1 flex-basis-45">
						<CoreCurrencyPercentageInput
							class="flex-grow-1 flex-shrink-1"
							v-model="unitTypeStore.unitTypeData.residual_value_day_1"
							:baseValue="unitValue"
							:validator="v$.residual_value_day_1"
							:customNotificationMessage="
								v$.residual_value_day_1 !== undefined &&
								v$.residual_value_day_1.notGreaterThan.$invalid
									? v$.residual_value_day_1.notGreaterThan.$message
									: null
							"
							:currency="currencyCode"
							:currencyDigits="RV_CURRENCY_DIGITS"
							:disabled="isWaitingForDefaultShare"
						/>
						<div
							class="rv-lock-spacer"
							aria-hidden="true"
						>
							<CoreIcon
								name="arrow-down-right"
								:size="28"
								class="rv-lock-arrow"
							/>
						</div>
					</div>
				</template>
			</CoreTwoColumnsRow>

			<CoreTwoColumnsRow class="mb-3">
				<template #left>
					<CoreBorderWrapper class="flex-grow-1 flex-shrink-1 flex-basis-55">
						{{ $t('§eq_type.residual_value_after_1_year') }}
						{{ isFieldRequired('residual_value_1_year') }}
					</CoreBorderWrapper>
				</template>
				<template #right>
					<div class="flex items-center gap-2 flex-grow-1 flex-shrink-1 flex-basis-45">
						<CoreCurrencyPercentageInput
							class="flex-grow-1 flex-shrink-1"
							v-model="unitTypeStore.unitTypeData.residual_value_1_year"
							:baseValue="unitValue"
							:validator="v$.residual_value_1_year"
							:customNotificationMessage="
								v$.residual_value_1_year !== undefined &&
								v$.residual_value_1_year.notGreaterThan.$invalid
									? v$.residual_value_1_year.notGreaterThan.$message
									: null
							"
							:currency="currencyCode"
							:currencyDigits="RV_CURRENCY_DIGITS"
							:disabled="isOneYearLocked || isWaitingForDefaultShare"
						/>
						<CoreIconButton
							name="toggleResidualValue1YearLock"
							color="primary-light"
							:isActive="isOneYearLocked"
							:icon="isOneYearLocked ? 'lock-outline' : 'lock-open-outline'"
							:iconHover="isOneYearLocked ? 'lock' : 'lock-open'"
							:iconSize="18"
							:tooltip="
								isOneYearLocked
									? '§eq_type.residual_value_1_year_locked_tooltip'
									: '§eq_type.residual_value_1_year_unlocked_tooltip'
							"
							@buttonClick="toggleOneYearLock"
						/>
					</div>
				</template>
			</CoreTwoColumnsRow>

			<div class="flex">
				<CoreButton
					label="§ui_button.calculate_rvt"
					name="calculateRvt"
					variant="secondary"
					icon="pi-table"
					:disabled="isCalculateDisabled"
					:loading="unitTypeStore.calculating"
					@click="calculate"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import Message from 'primevue/message'
import { useVuelidate } from '@vuelidate/core'
import { required, minValue, maxValue, helpers } from '@vuelidate/validators'
import { notGreaterThan } from '@/helpers/customValidators.js'
import { useUnitTypeStore } from '@/stores/unitType'
import { rvDepreciationPeriodOptions } from '@/helpers/rvDepreciationPeriodOptions.js'
import { roundHalfUp } from '@/helpers/roundHalfUp.js'
import { RV_CURRENCY_DIGITS } from '@/data/constants.js'
import CoreBorderWrapper from '@/components/core/CoreBorderWrapper.vue'
import CoreButton from '@/components/core/CoreButton.vue'
import CoreTwoColumnsRow from '@/components/core/CoreTwoColumnsRow.vue'
import CoreCurrencyPercentageInput from '@/components/core/CoreCurrencyPercentageInput.vue'
import CoreDropdown from '@/components/core/CoreDropdown.vue'
import CoreIconButton from '@/components/core/CoreIconButton.vue'
import CoreIcon from '@/components/core/CoreIcon.vue'
import ResidualValuesTable from './ResidualValuesTable.vue'

export default {
	name: 'InputsResidualValue',

	components: {
		Message,
		CoreBorderWrapper,
		CoreButton,
		CoreTwoColumnsRow,
		CoreCurrencyPercentageInput,
		CoreDropdown,
		CoreIconButton,
		CoreIcon,
		ResidualValuesTable,
	},

	data() {
		return {
			v$: {},
			unitTypeStore: useUnitTypeStore(),
			isUnmounted: false,
			RV_CURRENCY_DIGITS: RV_CURRENCY_DIGITS,
		}
	},

	watch: {
		isValid: {
			handler(newValue) {
				this.unitTypeStore.isValidResidualValueInputs = newValue
			},
			immediate: true,
		},

		unitValue() {
			this.applyDefaultResidualValues()
		},

		'unitTypeStore.isDefaultRvShareLoaded'(isLoaded) {
			if (isLoaded) {
				this.applyDefaultResidualValues()
			}
		},

		'unitTypeStore.unitTypeData.residual_value_day_1'(newValue) {
			if (this.isOneYearLocked) {
				this.unitTypeStore.unitTypeData.residual_value_1_year = newValue
			}
		},
	},

	computed: {
		isValid() {
			return !this.v$.$invalid
		},

		isOneYearLocked() {
			return this.unitTypeStore.isResidualValue1YearLocked
		},

		isWaitingForDefaultShare() {
			return !this.unitTypeStore.isDefaultRvShareLoaded
		},

		unitValue() {
			return Number(this.unitTypeStore.unitTypeData.value) || 0
		},

		currencyCode() {
			return this.unitTypeStore.unitTypeData.currency_code || 'EUR'
		},

		isCalculateDisabled() {
			return (
				!this.isValid ||
				this.unitValue < 1 ||
				this.unitTypeStore.calculating ||
				this.unitTypeStore.saving
			)
		},

		showScaledPanel() {
			return this.unitTypeStore.rvScaledFromValue !== null && this.unitValue >= 1
		},

		tableModeErrorMessage() {
			if (!this.unitTypeStore.hasRvt) {
				return null
			}
			const invalidRule = ['residual_value_day_1', 'residual_value_1_year']
				.map((key) => this.v$[key]?.notGreaterThan)
				.find((rule) => rule?.$invalid)
			return invalidRule ? invalidRule.$message : null
		},

		scaledPanelText() {
			const prices = {
				from: this.displayAsCurrency(this.unitTypeStore.rvScaledFromValue, 2, this.currencyCode),
				to: this.displayAsCurrency(this.unitValue, 2, this.currencyCode),
			}
			return this.unitTypeStore.hasRvt
				? this.$t('§eq_type.rvt_scaled_to_new_list_price', prices)
				: this.$t('§eq_type.rv_values_scaled_to_new_list_price', prices)
		},

		depreciationPeriodOptions() {
			return rvDepreciationPeriodOptions(this.$t)
		},

		rules() {
			return {
				rv_depreciation_period_years: {
					required,
				},
				residual_value_day_1: {
					required,
					notGreaterThan: helpers.withMessage(
						() => ({
							message: '§ui.input_error_rv_can_not_be_higher_than_eq_value',
						}),
						notGreaterThan(() => this.unitTypeStore.unitTypeData.value),
					),
					minValue: minValue(0),
					maxValue: maxValue(9999999999),
				},
				residual_value_1_year: {
					required,
					notGreaterThan: helpers.withMessage(
						() => ({
							message:
								'§ui.input_error_residual_value_1_year_can_not_be_higher_than_residual_value_day_1',
						}),
						notGreaterThan(() => this.unitTypeStore.unitTypeData.residual_value_day_1),
					),
					minValue: minValue(0),
					maxValue: maxValue(9999999999),
				},
			}
		},
	},

	mounted() {
		this.unitTypeStore.loadDefaultRvShare()
		this.applyDefaultResidualValues()
		this.$nextTick(() => {
			this.v$ = useVuelidate(this.rules, this.unitTypeStore.unitTypeData)
		})
	},

	beforeUnmount() {
		this.isUnmounted = true
	},

	methods: {
		applyDefaultResidualValues() {
			const unitValue = this.unitValue
			if (!unitValue || this.unitTypeStore.hasRvt || !this.unitTypeStore.isDefaultRvShareLoaded) {
				return
			}

			const data = this.unitTypeStore.unitTypeData
			const defaultValue = roundHalfUp(
				unitValue * this.unitTypeStore.defaultRvShare,
				RV_CURRENCY_DIGITS,
			)
			const filledKeys = []

			if (data.residual_value_day_1 == null) {
				data.residual_value_day_1 = defaultValue
				filledKeys.push('residual_value_day_1')
			}
			if (data.residual_value_1_year == null) {
				data.residual_value_1_year = this.isOneYearLocked
					? data.residual_value_day_1
					: defaultValue
				filledKeys.push('residual_value_1_year')
			}

			if (filledKeys.length) {
				this.unitTypeStore.rememberRvShares(filledKeys)
			}
		},

		async calculate() {
			const rvt = await this.unitTypeStore.calculateRVT()
			if (!rvt && !this.isUnmounted) {
				this.unitTypeStore.notification('error', '§ui_message.update_failed')
			}
		},

		toggleOneYearLock() {
			this.unitTypeStore.isResidualValue1YearLocked =
				!this.unitTypeStore.isResidualValue1YearLocked

			if (this.unitTypeStore.isResidualValue1YearLocked) {
				this.unitTypeStore.unitTypeData.residual_value_1_year =
					this.unitTypeStore.unitTypeData.residual_value_day_1
			}
		},
	},
}
</script>

<style scoped>
.residual-value-box {
	padding: var(--space-6);
	border: 1px solid var(--color-neutral-medium);
	border-radius: 8px;
	background-color: var(--color-neutral-200);
}

.rv-expand-enter-active,
.rv-expand-leave-active {
	overflow: hidden;
	transition:
		opacity 0.25s ease,
		transform 0.25s ease,
		max-height 0.3s ease,
		margin-top 0.3s ease,
		padding-top 0.3s ease,
		padding-bottom 0.3s ease;
}

.rv-expand-enter-from,
.rv-expand-leave-to {
	opacity: 0;
	transform: translateY(-8px);
	max-height: 0;
	margin-top: 0;
	padding-top: 0;
	padding-bottom: 0;
}

.rv-expand-enter-to,
.rv-expand-leave-from {
	opacity: 1;
	max-height: 500px;
}

:deep(.core-currency-percentage-input__currency) {
	flex: 1 1 60%;
}

:deep(.core-currency-percentage-input__percentage) {
	flex: 1 1 40%;
}

.rv-lock-spacer {
	flex: 0 0 40px;
	align-self: stretch;
	position: relative;
}

.rv-lock-arrow {
	position: absolute;
	left: -2px;
	bottom: -26px;
	color: var(--color-secondary-200);
	pointer-events: none;
}
</style>
